import { getAuthCookie } from "../../utils/authCookies";
import { resolvePostTypeVendorOrganizationId } from "../../api/postTypeApiCalls";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export type TaxRule = {
  _id: string;
  country: string;
  state: string;
  stateCode: string;
  county: string;
  countyCode: string;
  zipCodes: string[];
  taxRate: number;
  comments: string;
  status: string;
};

export type TaxGroup = {
  id: string;
  name: string;
  tax_rules: TaxRule[];
  status: string;
};

export function resolveTaxOrganizationId(fallback?: string | null): string {
  return resolvePostTypeVendorOrganizationId(fallback ?? getAuthCookie("organizationId"));
}

export const orgTaxGroupsBase = (organizationId: string) =>
  `${API_BASE_URL}/organizations/${encodeURIComponent(organizationId)}/tax-groups`;

export const orgTaxRulesBase = (organizationId: string) =>
  `${API_BASE_URL}/organizations/${encodeURIComponent(organizationId)}/tax-rules`;

export const orgTaxesBase = (organizationId: string) =>
  `${API_BASE_URL}/organizations/${encodeURIComponent(organizationId)}/taxes`;

export const orgGeoLocationsBase = (organizationId: string) =>
  `${API_BASE_URL}/organizations/${encodeURIComponent(organizationId)}/geo-locations`;

export function unwrapTaxList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object" && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: T[] }).data;
  }
  return [];
}

export function unwrapTaxEntity<T>(data: unknown): T {
  if (data && typeof data === "object" && "data" in (data as object) && (data as { data?: unknown }).data != null) {
    return (data as { data: T }).data;
  }
  return data as T;
}

const jsonOrThrow = async (res: Response) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || (data && (data.error || data.message))) {
    throw new Error(data.error || data.message || `HTTP ${res.status}`);
  }
  return data;
};

export async function fetchTaxGroups(token: string, organizationId?: string): Promise<TaxGroup[]> {
  const orgId = resolveTaxOrganizationId(organizationId);
  if (!orgId) throw new Error("Organization not found");
  const res = await fetch(orgTaxGroupsBase(orgId), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  const data = await jsonOrThrow(res);
  return unwrapTaxList<TaxGroup>(data);
}

export async function fetchTaxRules(token: string, organizationId?: string): Promise<TaxRule[]> {
  const orgId = resolveTaxOrganizationId(organizationId);
  if (!orgId) throw new Error("Organization not found");
  const res = await fetch(orgTaxRulesBase(orgId), {
    method: "GET",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  const data = await jsonOrThrow(res);
  return unwrapTaxList<TaxRule>(data);
}
